import { yupResolver } from '@hookform/resolvers/yup';
import { Button, type FormSelectOption, Modal, ModalCloseButton, NotificationDispatch, type SelectOption, TextField } from '@tryhackme/thm-ui-components';
import { useState } from 'react';
import type { MultiValue } from 'react-select';
import { Controller, useForm } from 'src/common/form';
import { isQueryErrorWithMessage } from 'src/common/helpers/slices';
import { DEFAULT_INPUT_VALUES, inputModalFormValidation } from 'src/features/admin/manage-users/modals/input-modal/input-modal.constants';
import { StyledActionButtonsWrapper, StyledContent, StyledInput, StyledInputWrapper, StyledText, StyledTitle, StyledWrapper } from 'src/features/admin/manage-users/modals/modals.styles';
import type { InputModalProps } from '../modal.types';
export const InputModal = ({
  isOpen,
  setIsOpen,
  userId,
  buttonText,
  buttonColor,
  title,
  description,
  mutation,
  setSearchQuery,
  searchQuery,
  currentValue,
  triggerElement,
  successMessage,
  inputFieldOptions,
  label,
  id,
  valueKey,
  placeholder,
  type = 'text',
  isSearchable = false,
  isMulti = false,
  defaultValues,
  onSuccess
}: InputModalProps) => {
  const [mutationTrigger, {
    isLoading
  }] = mutation();
  const [currentFormValues, setCurrentFormValues] = useState(defaultValues || DEFAULT_INPUT_VALUES);
  const {
    register,
    handleSubmit,
    reset,
    getValues,
    control,
    setValue,
    formState: {
      isValid
    }
  } = useForm({
    resolver: yupResolver(inputModalFormValidation),
    mode: 'onChange',
    values: currentFormValues
  });
  const closeModal = () => {
    setIsOpen(!isOpen);
    reset();
  };
  const onSubmit = async () => {
    const {
      value
    } = getValues();
    try {
      await mutationTrigger({
        userId,
        [valueKey]: value
      }).unwrap();
      if (searchQuery && setSearchQuery && currentValue && searchQuery === currentValue && value) {
        setSearchQuery(value);
      }
      NotificationDispatch('success', successMessage);
      onSuccess?.(value);
      closeModal();
    } catch (error: unknown) {
      NotificationDispatch('error', isQueryErrorWithMessage(error) ? error.data.message : 'An unknown error has occurred');
    }
  };
  const handleDropdownChange = (value?: SelectOption | MultiValue<FormSelectOption>) => {
    if (Array.isArray(value)) {
      const valueToSet = value.map((option: FormSelectOption) => option.value).join(',');
      setValue('value', valueToSet, {
        shouldValidate: true
      });
      setCurrentFormValues({
        value: valueToSet
      });
    } else if (value && 'value' in value) {
      setValue('value', value.value, {
        shouldValidate: true
      });
      setCurrentFormValues({
        value: value.value
      });
    }
  };
  if (isOpen && defaultValues && JSON.stringify(currentFormValues) !== JSON.stringify(defaultValues)) {
    setCurrentFormValues(defaultValues);
  }
  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    handleSubmit(onSubmit)(e).catch(() => {});
  };
  return <Modal open={isOpen} onOpenChange={closeModal} isSmall triggerElement={triggerElement} overflowVisible data-sentry-element="Modal" data-sentry-component="InputModal" data-sentry-source-file="input-modal.tsx">
      <form onSubmit={handleFormSubmit}>
        <StyledWrapper data-sentry-element="StyledWrapper" data-sentry-source-file="input-modal.tsx">
          <StyledTitle data-sentry-element="StyledTitle" data-sentry-source-file="input-modal.tsx">{title}</StyledTitle>
          <StyledContent data-sentry-element="StyledContent" data-sentry-source-file="input-modal.tsx">
            <StyledText data-sentry-element="StyledText" data-sentry-source-file="input-modal.tsx">{description}</StyledText>
            <StyledInputWrapper data-sentry-element="StyledInputWrapper" data-sentry-source-file="input-modal.tsx">
              <StyledInput data-sentry-element="StyledInput" data-sentry-source-file="input-modal.tsx">
                {label && <StyledText>{label}</StyledText>}
                {type === 'select' && inputFieldOptions ? <Controller name="value" control={control} render={({
                field: {
                  ref,
                  value,
                  onChange,
                  name,
                  ...rest
                }
              }) => <TextField variant="select" isMulti={isMulti} isSearchable={isSearchable} options={inputFieldOptions} selected={(() => {
                if (isMulti) {
                  const selectedValues = value ? value.split(',') : [];
                  return selectedValues.map(valueOption => inputFieldOptions.find(option => option.value === valueOption)).filter(Boolean) as MultiValue<FormSelectOption>;
                }
                return inputFieldOptions.find(option => option.value === value);
              })()} handleSelection={selectedValue => {
                handleDropdownChange(selectedValue);
              }} placeholder={placeholder} innerRef={ref} {...rest} />} /> : <TextField {...register('value')} id={`input-modal-${id}`} variant="input" type={type} placeholder={placeholder} />}
              </StyledInput>
            </StyledInputWrapper>
          </StyledContent>
          <StyledActionButtonsWrapper data-sentry-element="StyledActionButtonsWrapper" data-sentry-source-file="input-modal.tsx">
            <ModalCloseButton text="Cancel" data-sentry-element="ModalCloseButton" data-sentry-source-file="input-modal.tsx" />
            <Button type="submit" disabled={!isValid || isLoading} isLoading={isLoading} variant="solid" color={buttonColor} data-sentry-element="Button" data-sentry-source-file="input-modal.tsx">
              {buttonText}
            </Button>
          </StyledActionButtonsWrapper>
        </StyledWrapper>
      </form>
    </Modal>;
};